// JWT Token Manager for Legacy Site
// This module manages JWT tokens for the legacy site to interact with SPA API endpoints

import { safeStorageGet, safeStorageSet, safeStorageRemove } from './safe-storage.js';

// The single localStorage key holding auth/account/preferences state. This
// module is the owning boundary for the web token storage scheme.
const TOKEN_STORAGE_KEY = 'redux-state';

// Debug flag - set to true to enable console logging
export const JWT_DEBUG = false;

/**
 * Debug logging function that only logs when JWT_DEBUG is true
 * @param {string} message - The message to log
 * @param {any} [data] - Optional data to log
 */
export function debugLog(message, data) {
  if (JWT_DEBUG) {
    if (data !== undefined) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
}

/**
 * Initialize JWT token for the current user session
 * Checks if a valid token exists in localStorage, and if not, fetches a new one
 * @returns {Promise<Object|null>} The token data or null if not logged in
 */
export async function initializeJwtToken() {
  debugLog('[JWT] Initializing JWT token');
  const userId = getCurrentUserId();
  debugLog('[JWT] Current user ID:', userId);

  if (!userId) {
    debugLog('[JWT] No user ID found, clearing token');
    clearJwtToken();
    return null;
  }

  // Check if token exists and belongs to the current user
  debugLog('[JWT] Checking for existing token in localStorage');
  const tokenData = getJwtToken();
  if (tokenData && tokenData.auth && tokenData.auth.token) {
    // Verify the token belongs to the current user
    const storedUserId = tokenData.account?.userId;
    if (storedUserId && String(storedUserId) === String(userId)) {
      debugLog('[JWT] Valid token found in localStorage for current user');
      return tokenData;
    } else {
      debugLog('[JWT] Token found but belongs to different user (stored: ' + storedUserId + ', current: ' + userId + '), clearing');
      clearJwtToken();
    }
  }

  debugLog('[JWT] No valid token found, fetching new token from server');
  // Fetch new token
  try {
    const csrfToken = getCsrfToken();
    debugLog('[JWT] Sending request to create-api-token-by-session endpoint');
    const response = await fetch('/app/create-api-token-by-session', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        csrf_token: csrfToken,
      }),
    });

    const data = await response.json();
    debugLog('[JWT] Received response from server:', data.success ? 'success' : 'failure');

    if (data.success && data.token) {
      debugLog('[JWT] Successfully received token from server');
      const tokenData = {
        auth: {
          token: data.token
        },
        account: {
          // Account data could be added here if needed
          userId: data.user_id
        }
      };
      setJwtToken(tokenData);
      return tokenData;
    } else {
      console.warn('[JWT] Failed to get token from server, response:', data);
    }
  } catch (error) {
    console.error('[JWT] Error fetching JWT token:', error);
  }

  debugLog('[JWT] Token initialization failed');
  return null;
}

/**
 * Get JWT token from localStorage
 * @returns {Object|null} The token data or null if not found
 */
export function getJwtToken() {
  debugLog('[JWT] Getting token from localStorage');
  try {
    const tokenData = JSON.parse(safeStorageGet(TOKEN_STORAGE_KEY));
    if (tokenData) {
      debugLog('[JWT] Token found in localStorage');
    } else {
      debugLog('[JWT] No token found in localStorage');
    }
    return tokenData;
  } catch (e) {
    console.error('[JWT] Error parsing token from localStorage:', e);
    return null;
  }
}

/**
 * Set JWT token in localStorage
 * @param {Object} tokenData The token data to store
 */
export function setJwtToken(tokenData) {
  debugLog('[JWT] Storing token in localStorage');
  safeStorageSet(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  debugLog('[JWT] Token successfully stored in localStorage');
}

/**
 * Clear JWT token, account metadata, and legacy preference state from localStorage
 *
 * Every path out of here ends with the credential gone: a value that will not
 * parse is discarded outright rather than rewritten, and a rewrite that cannot
 * be persisted falls back to dropping the key.
 */
export function clearJwtToken() {
  debugLog('[JWT] Clearing token from localStorage');

  let state;
  try {
    state = JSON.parse(safeStorageGet(TOKEN_STORAGE_KEY)) || {};
  } catch {
    safeStorageRemove(TOKEN_STORAGE_KEY);
    debugLog('[JWT] Stored state was unparseable, key dropped');
    return;
  }

  delete state.auth;
  delete state.account;
  delete state.preferences;

  // A rejected write leaves the previous value in place, token and all, so
  // fall back to dropping the key rather than let a stale credential outlive
  // the clear. This runs on the logged-out and wrong-user paths, and what is
  // left behind is read by getAuthToken() on the next page.
  if (Object.keys(state).length > 0 && safeStorageSet(TOKEN_STORAGE_KEY, JSON.stringify(state))) {
    debugLog('[JWT] Token successfully cleared from localStorage');
    return;
  }

  safeStorageRemove(TOKEN_STORAGE_KEY);
  debugLog('[JWT] Token successfully cleared from localStorage');
}

/**
 * Get current user ID from meta tag
 * @returns {string|null} The user ID or null if not found
 */
function getCurrentUserId() {
  debugLog('[JWT] Getting current user ID from meta tag');
  const metaTag = document.querySelector('meta[name="user-id"]');
  const userId = metaTag ? metaTag.getAttribute('content') : null;
  if (userId) {
    debugLog('[JWT] User ID found:', userId);
  } else {
    debugLog('[JWT] No user ID found in meta tag');
  }
  return userId;
}

/**
 * Get CSRF token from meta tag
 * @returns {string|null} The CSRF token or null if not found
 */
function getCsrfToken() {
  debugLog('[JWT] Getting CSRF token from meta tag');
  const metaTag = document.querySelector('meta[name="csrf_token"]');
  const csrfToken = metaTag ? metaTag.getAttribute('content') : null;
  if (csrfToken) {
    debugLog('[JWT] CSRF token found');
  } else {
    debugLog('[JWT] No CSRF token found in meta tag');
  }
  return csrfToken;
}
